import { ClockIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';
import { ArrowTrendingDownIcon, CurrencyDollarIcon } from '@heroicons/react/16/solid';

export const PriceInfo: React.FC<{
  price: number;
  originalPrice: number;
  earlyBird?: number;
  earlyBirdEndDate?: string;
  locale?: string;
  variety?: API.MembershipProductOffer;
  stats?: API.MembershipProductStats;
  discount?: number;
  balance?: number;
  loading?: boolean;
}> = ({
  price,
  earlyBird,
  locale,
  originalPrice,
  variety,
  // stats,
  discount = 0,
  balance = 0,
  earlyBirdEndDate,
  loading = false,
}) => {
  const displayPrice = originalPrice + (variety?.addOn?.price || 0);
  let balanceUsed = Math.min(balance, price);
  let priceSuffix = '';
  const isAnnual = variety?.time?.subscription?.interval === '1 years';
  if (variety?.time?.subscription?.interval) {
    priceSuffix = '/ month';
    balanceUsed = 0; // cannot use balance on Subscriptions
    // @todo https://github.com/parkerrome/raspicks/issues/897
  }

  return (
    <div className="mb-2">
      {!!earlyBird && (
        <div className="mb-3">
          {/* Mobile: compact single-line pill */}
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 sm:hidden">
            <ClockIcon height={11} className="size-2.5 shrink-0 text-white" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase text-white">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
          {/* sm+: single-line pill badge */}
          <p className="hidden w-fit items-center gap-1.5 rounded-full bg-red-500 px-3 py-1 sm:inline-flex">
            <ClockIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
        </div>
      )}
      <div className="flex items-baseline">
        {!!earlyBird && (
          <p
            className={twMerge(
              'font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100',
              (!!discount || !!balance) &&
                'font-normal text-xl text-zinc-500 line-through dark:text-zinc-300',
            )}
          >
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(displayPrice)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
        )}
        <p
          className={twMerge(
            'font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100',
            earlyBird && 'text-xl! font-medium! text-zinc-400! ms-3 line-through',
            (!!discount || !!balance) && 'font-normal text-xl text-zinc-500 dark:text-zinc-300',
          )}
        >
          <Skeleton loading={loading}>
            <span className={twMerge((!!discount || !!balance) && 'line-through')}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(
                isAnnual ? (displayPrice + (earlyBird || 0)) / 12 : displayPrice + (earlyBird || 0),
              )}
            </span>
          </Skeleton>
          {priceSuffix && (
            <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
              {priceSuffix}
            </span>
          )}
        </p>
      </div>
      {(!!discount || !!balance) && (
        <div className="mt-4 text-emerald-700 dark:text-emerald-100">
          <p className="flex flex-row space-x-1 font-medium text-sm">
            <ArrowTrendingDownIcon height={16} className="size-4" />
            <span>Your price {!!discount && `(${discount * 100}% off)`}</span>
          </p>
          <p className="flex flex-row items-baseline space-x-2 font-semibold text-3xl tracking-tight">
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(isAnnual ? (price - balanceUsed) / 12 : price - balanceUsed)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
          {!!balance && !!balanceUsed && (
            <div className="mt-2 flex flex-row items-center space-x-1 font-medium text-xs">
              <CurrencyDollarIcon height={16} className="size-4 shrink-0" />
              <span>
                With{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balanceUsed)}{' '}
                of your{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balance)}{' '}
                balance applied
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
